import React, { useState, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  DialogActions,
  Button,
  IconButton,
  Grid,
  Box,
  CircularProgress,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { DialogInput } from 'screens/components/advanced';
import DialogHeaderWithClose from 'screens/components/basic/DialogHeaderWithClose';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImageUploader = ({
  multiple = false,
  maxFiles = 1,
  maxSizeMB = 5,
  acceptedFormats = ['image/*'],
  existingImages = [],
  onUpload,
  onDelete,
  label = 'Upload Images',
  existingDisplayModes = [],
  aspectRatio = { width: 16, height: 9 },
}) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [displayModes, setDisplayModes] = useState({});
  const [crop, setCrop] = useState({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    aspect: aspectRatio.width / aspectRatio.height,
  });
  const [currentImage, setCurrentImage] = useState(null);
  const [croppingIndex, setCroppingIndex] = useState(-1);
  const imgRef = useRef(null);
const [completedCrop, setCompletedCrop] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFiles([]);
    setPreviews([]);
    setDisplayModes({});
    setCurrentImage(null);
    setCroppingIndex(-1);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    if (newFiles.length + existingImages.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    const oversizedFiles = newFiles.filter(
      (file) => file.size > maxSizeMB * 1024 * 1024
    );
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the maximum size of ${maxSizeMB}MB.`);
      return;
    }

    const invalidFormatFiles = newFiles.filter(
      (file) => !acceptedFormats.some((format) => file.type.match(format))
    );
    if (invalidFormatFiles.length > 0) {
      alert('Some files have invalid formats.');
      return;
    }

    setFiles(newFiles);
    startCropping(newFiles[0], 0);
  };

  const startCropping = (file, index) => {
    const reader = new FileReader();
    reader.onload = () => {
      setTimeout(() => {
        setCurrentImage(reader.result);
        setCroppingIndex(index);
      }, 100);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = (img) => {
    imgRef.current = img;
    const newCrop = {
      unit: '%',
      x: 10,
      y: 10,
      width: 80,
      aspect: aspectRatio.width / aspectRatio.height,
    };
    setCrop(newCrop);
  };

  const handleCropComplete = async () => {
    if (!imgRef.current || !crop.width || !crop.height) return;

    const croppedImageUrl = await getCroppedImg(
      imgRef.current,
      crop,
      'cropped-image.jpeg'
    );

    const newPreviews = [...previews];
    if (croppingIndex >= newPreviews.length) {
      newPreviews.push({
        id: URL.createObjectURL(files[croppingIndex]),
        url: croppedImageUrl,
        file: files[croppingIndex],
        displayMode: 0,
      });
    } else {
      newPreviews[croppingIndex] = {
        ...newPreviews[croppingIndex],
        url: croppedImageUrl,
      };
    }

    setPreviews(newPreviews);
    setCurrentImage(null);
    setCroppingIndex(-1);
  };

  const getCroppedImg = (image, crop, fileName, outputWidth = 2000, outputHeight = 600) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
  
    canvas.width = outputWidth;
    canvas.height = outputHeight;
  
    const ctx = canvas.getContext('2d');
  
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      outputWidth,
      outputHeight
    );
  
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(URL.createObjectURL(blob));
        },
        'image/jpeg',
        0.95
      );
    });
  };
  

  const handleDisplayModeChange = (id, mode) => {
    setDisplayModes((prev) => ({
      ...prev,
      [id]: parseInt(mode),
    }));
  };

  const handleRemovePreview = (id) => {
    setPreviews((prev) => prev.filter((p) => p.id !== id));
    setFiles((prev) => prev.filter((f) => URL.createObjectURL(f) !== id));
    setDisplayModes((prev) => {
      const newModes = { ...prev };
      delete newModes[id];
      return newModes;
    });
  };

  const handleUpload = () => {
    setIsUploading(true);

    setTimeout(() => {
      const newPreviewsWithModes = previews.map((p) => ({
        ...p,
        displayMode: displayModes[p.id] || 0,
      }));

      const duplicateModes = newPreviewsWithModes.some((p) =>
        existingDisplayModes.includes(p.displayMode)
      );

      if (duplicateModes) {
        alert(
          'Each display mode (Desktop, Tablet, Mobile) can only be used once.'
        );
        setIsUploading(false);
        return;
      }

      onUpload(files, newPreviewsWithModes);
      setIsUploading(false);
      handleClose();
    }, 1000);
  };

  const getDisplayModeText = (mode) => {
    switch (mode) {
      case 0:
        return 'Desktop';
      case 1:
        return 'Tablet';
      case 2:
        return 'Mobile';
      default:
        return 'Desktop';
    }
  };

  return (
    <div>
      <Button
        component="label"
        variant="contained"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpen}
      >
        {label}
      </Button>

      <DialogInput
        title={
          <DialogHeaderWithClose
            text={label}
            classicClass="posDialogClassic"
            handleClose={handleClose}
          />
        }
        className="no_btns_dialog"
        modal={true}
        open={open}
        autoScrollBodyContent={true}
        style={{ minWidth: '70% !important' }}
      >
        {currentImage ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Crop Image to 4:1 Aspect Ratio (recommended size is 1200px * 300px)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please select the area to crop
              </Typography>
            </Box>
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio.width / aspectRatio.height}
            >
              <img
                src={currentImage}
                alt="Crop me"
                ref={imgRef}
                onLoad={(e) => onImageLoad(e.currentTarget)}
                style={{ maxWidth: '100%' }}
              />
            </ReactCrop>

            <DialogActions>
              <Button onClick={() => setCurrentImage(null)}>Cancel</Button>
              <Button onClick={handleCropComplete} variant="contained">
                Apply Crop
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Max files: {maxFiles} | Max size: {maxSizeMB}MB | Formats:{' '}
                {acceptedFormats.join(', ')}
                {aspectRatio &&
                  ` | Aspect ratio: ${aspectRatio.width}:${aspectRatio.height}`}
              </Typography>
            </Box>

            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 2 }}
            >
              Select Files
              <VisuallyHiddenInput
                type="file"
                multiple={multiple}
                onChange={handleFileChange}
                accept={acceptedFormats.join(',')}
              />
            </Button>

            <Grid container spacing={2}>
              {existingImages.map((img) => (
                <Grid item xs={12} sm={6} md={4} key={img.id}>
                  <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                    <img
                      src={img.url}
                      alt="Preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'red',
                      }}
                      onClick={() => onDelete(img.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}

              {previews.map((preview, index) => (
                <Grid item xs={12} sm={6} md={4} key={preview.id}>
                  <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                    <img
                      src={preview.url}
                      alt="Preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'red',
                      }}
                      onClick={() => handleRemovePreview(preview.id)}
                    >
                      <DeleteIcon />
                    </IconButton>

                    <FormControl
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        minWidth: 120,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                      }}
                      size="small"
                    >
                      <Select
                        value={displayModes[preview.id] || 0}
                        onChange={(e) =>
                          handleDisplayModeChange(preview.id, e.target.value)
                        }
                        displayEmpty
                      >
                        <MenuItem
                          value={0}
                          disabled={existingDisplayModes.includes(0)}
                        >
                          Desktop{' '}
                          {existingDisplayModes.includes(0) &&
                            '(Already exists)'}
                        </MenuItem>
                        <MenuItem
                          value={1}
                          disabled={existingDisplayModes.includes(1)}
                        >
                          Tablet{' '}
                          {existingDisplayModes.includes(1) &&
                            '(Already exists)'}
                        </MenuItem>
                        <MenuItem
                          value={2}
                          disabled={existingDisplayModes.includes(2)}
                        >
                          Mobile{' '}
                          {existingDisplayModes.includes(2) &&
                            '(Already exists)'}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                onClick={handleUpload}
                disabled={previews.length === 0 || isUploading}
                variant="contained"
              >
                {isUploading ? <CircularProgress size={24} /> : 'Upload'}
              </Button>
            </DialogActions>
          </>
        )}
      </DialogInput>
    </div>
  );
};

export default ImageUploader;
